import {
  Award,
  Boxes,
  Contact,
  FolderTree,
  Gift,
  ImageIcon,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  type LucideIcon,
  Megaphone,
  MessageCircle,
  Newspaper,
  PackageSearch,
  Palette,
  Percent,
  ShieldCheck,
  ShoppingBag,
  Truck,
  Warehouse,
} from 'lucide-react';

/**
 * The console's navigation, mirroring ctrl-saas's team-account sidebar.
 *
 * The grouping is deliberate and carried over verbatim: it follows how a
 * dispensary actually works — selling, what they sell, keeping people coming
 * back, and what shoppers see — rather than the shape of the codebase.
 *
 * Settings is absent here on purpose. It opens a dialog rather than navigating,
 * so it is rendered separately in the sidebar footer area.
 *
 * `resource` names the per-member capability that gates the section, matching
 * accounts_memberships.resources and the RLS policies in
 * 20260828020000_resource_capability_rls.sql. Items the member cannot open are
 * hidden rather than shown dead — ctrl-saas made the same choice for the
 * settings cards, for the same reason.
 */

export interface NavItem {
  title: string;
  to: string;
  icon: LucideIcon;
  /** Capability required to open it; undefined means every member may. */
  resource?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Sales',
    items: [
      { title: 'Dashboard', to: '/', icon: LayoutDashboard },
      { title: 'Orders', to: '/orders', icon: ShoppingBag, resource: 'orders' },
      { title: 'Deliveries', to: '/deliveries', icon: Truck, resource: 'delivery' },
      { title: 'Queue', to: '/queue', icon: ListChecks, resource: 'checkin' },
      { title: 'Customers', to: '/customers', icon: Contact, resource: 'customers' },
      { title: 'Messages', to: '/messages', icon: MessageCircle, resource: 'support' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { title: 'Products', to: '/products', icon: Boxes, resource: 'catalog' },
      { title: 'Collections', to: '/collections', icon: Layers, resource: 'catalog' },
      { title: 'Taxonomy', to: '/taxonomy', icon: FolderTree, resource: 'catalog' },
      { title: 'Inventory', to: '/inventory', icon: Warehouse, resource: 'inventory' },
      { title: 'Packages', to: '/packages', icon: PackageSearch, resource: 'inventory' },
    ],
  },
  {
    label: 'Retention',
    items: [
      { title: 'Discounts', to: '/discounts', icon: Percent, resource: 'discounts' },
      { title: 'Gift Cards', to: '/gift-cards', icon: Gift, resource: 'payments' },
      { title: 'Loyalty Program', to: '/loyalty', icon: Award, resource: 'customers' },
    ],
  },
  {
    label: 'Content',
    items: [
      { title: 'Storefront', to: '/pages', icon: LayoutTemplate, resource: 'catalog' },
      { title: 'Designs', to: '/designs', icon: Palette, resource: 'catalog' },
      { title: 'Blog', to: '/blog', icon: Newspaper, resource: 'catalog' },
      { title: 'Media Library', to: '/media', icon: ImageIcon, resource: 'catalog' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { title: 'Marketing', to: '/marketing', icon: Megaphone, resource: 'marketing' },
      { title: 'Compliance', to: '/compliance', icon: ShieldCheck, resource: 'compliance' },
    ],
  },
];

/** Flat lookup, so a page can name its own section for the breadcrumb. */
const BY_PATH = new Map(NAV_GROUPS.flatMap((group) => group.items).map((item) => [item.to, item]));

/**
 * The nav item a pathname belongs to.
 *
 * Matches the longest registered prefix so a detail route
 * (`/orders/abc123`) resolves to its list section (`Orders`) — which is what
 * the breadcrumb's middle segment names.
 */
export function navItemForPath(pathname: string): NavItem | undefined {
  const exact = BY_PATH.get(pathname);
  if (exact) return exact;

  let best: NavItem | undefined;

  for (const [path, item] of BY_PATH) {
    if (path === '/') continue;
    if (pathname.startsWith(`${path}/`) && (!best || path.length > best.to.length)) {
      best = item;
    }
  }

  return best;
}
